import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { assistant as assistantApi } from "../api/resources";
import { AssistantPanel } from "../components/assistant/AssistantPanel";
import { ClassificationReview } from "../components/assistant/ClassificationReview";
import { Card, EmptyState, ErrorBanner, Loading, PageHeader } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { useOrganizations } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";
import type { AssistantConversation } from "../lib/types";

/**
 * The assistant's own page: a conversation on the left, this person's history on the right.
 *
 * The history is theirs alone. A colleague's conversation is not listed and not reachable — the
 * API answers 404 either way — so nothing here needs to hide anything.
 */

function money(amount: string | null, currency: string, unpriced: number): string {
  if (unpriced > 0) return "unpriced";
  if (amount === null) return "—";
  const value = Number(amount);
  return `${currency === "USD" ? "$" : `${currency} `}${value.toFixed(value < 1 ? 4 : 2)}`;
}

const GENERAL_SUGGESTIONS = [
  "What have we learned so far?",
  "What is still missing?",
  "Are there any contradictions?",
  "What does Enterprise Context publish for us?",
];

export default function AssistantPage() {
  const { current } = useOrganizations();
  const { can } = useAuth();
  const organizationId = current?.id;
  const [nonce, setNonce] = useState(0);
  const [query, setQuery] = useState("");
  const [panelKey, setPanelKey] = useState(0);
  const [error, setError] = useState<string>();

  const history = useAsync(
    () =>
      organizationId
        ? assistantApi.conversations(organizationId, { q: query || undefined, limit: 30 })
        : Promise.resolve(undefined),
    [organizationId, query, nonce],
  );

  const remove = useCallback(
    async (conversation: AssistantConversation) => {
      if (!organizationId) return;
      try {
        await assistantApi.deleteConversation(organizationId, conversation.id);
        setNonce((value) => value + 1);
      } catch {
        setError("That conversation could not be removed.");
      }
    },
    [organizationId],
  );

  const togglePin = useCallback(
    async (conversation: AssistantConversation) => {
      if (!organizationId) return;
      await assistantApi
        .updateConversation(organizationId, conversation.id, {
          pinned: !conversation.pinned,
        })
        .catch(() => setError("That could not be changed."));
      setNonce((value) => value + 1);
    },
    [organizationId],
  );

  if (!organizationId) {
    return <EmptyState title="No organization selected" description="Choose one to continue." />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Discovery Assistant"
        description="Ask about a question, what has been learned, or what governed context says. It helps you say what you know — it never decides."
        actions={
          <button
            type="button"
            className="btn"
            onClick={() => {
              setPanelKey((value) => value + 1);
              setNonce((value) => value + 1);
            }}
          >
            New conversation
          </button>
        }
      />

      <ErrorBanner error={error} />

      <div className="with-assistant">
        <div className="stack">
          <Card
            title="Your conversations"
          >
            <p className="small muted" style={{ margin: "0 0 8px" }}>
              Only yours. A colleague's chat is not listed and not reachable.
            </p>
            <input
              type="search"
              placeholder="Search titles and messages…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search conversations"
            />
            {history.loading ? <Loading /> : null}
            <ErrorBanner error={history.error} />
            {history.data && history.data.items.length === 0 ? (
              <EmptyState
                title={query ? "Nothing matches" : "No conversations yet"}
                description={
                  query
                    ? "Try a different word."
                    : "Ask something in the panel to start one."
                }
              />
            ) : null}
            {history.data && history.data.items.length > 0 ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Conversation</th>
                      <th>Mode</th>
                      <th>Messages</th>
                      <th>Cost</th>
                      <th>Last activity</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {history.data.items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.title ?? <span className="muted">Untitled</span>}
                          {row.pinned ? <span className="badge badge--accent">Pinned</span> : null}
                          {row.error_count > 0 ? (
                            <div className="tiny faint">{row.last_error}</div>
                          ) : null}
                        </td>
                        <td className="small">{row.mode.replace(/_/g, " ")}</td>
                        <td className="small">{row.message_count}</td>
                        <td className="small">
                          {money(row.total_cost_amount, row.currency, row.unpriced_calls)}
                        </td>
                        <td className="small muted">
                          {row.last_message_at
                            ? new Date(row.last_message_at).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            onClick={() => togglePin(row)}
                          >
                            {row.pinned ? "Unpin" : "Pin"}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            onClick={() => remove(row)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <p className="tiny muted">
              Deleting a conversation removes what was said. It never removes what it cost — that
              stays on the <Link to="/ai">AI &amp; cost</Link> page.
            </p>
          </Card>

          {organizationId && can(organizationId, "knowledge:verify") ? (
            <ClassificationReview organizationId={organizationId} needsReviewOnly />
          ) : null}
        </div>

        <div className="with-assistant__panel">
          <AssistantPanel
            key={panelKey}
            organizationId={organizationId}
            mode="general_chat"
            suggestions={GENERAL_SUGGESTIONS}
            onDraftSaved={() => setNonce((value) => value + 1)}
          />
        </div>
      </div>
    </div>
  );
}
